"use client";

import useDates from "@/hooks/useDates";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  NumberField,
  Typography,
} from "@repo/ui";

import { Building, CheckCircle, Monitor, Timer, User } from "lucide-react";
import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import useCart from "@/hooks/useCart";
import { formatCurrency } from "@/lib/helpers";
import NoScheduleCartDownloadSyllabus from "../common/NoScheduleCartDownloadSyllabus";
import moment from "moment";
import { useCountryCode } from "@/hooks/useCountryCode";
import { useAppSelector } from "@/store/hooks";
import RegistrationCloseTimer from "../common/RegistrationCloseTimer";
import clsx from "clsx";

type FastFillingScheduleProps = {
  data?: ServiceEvent;
};

const FastFillingSchedule = ({ data }: FastFillingScheduleProps) => {
  const [quantity, setQuantity] = useState<number>(1);
  const status = useAppSelector((state) => state.cart.status);
  const { country } = useCountryCode();
  const router = useRouter();
  const { addToCart } = useCart({
    onSuccess: () => {
      router.push(`/${country}/order-summary`);
    },
  });

  const { formattedDate, weekLabel, numberOfDays } = useDates(
    data?.eventDates || []
  );

  const hours = useMemo(() => {
    if (data?.eventDetails.startTime && data.eventDetails.endTime) {
      const startTime = moment(data?.eventDetails.startTime, "HH:mm");
      const endTime = moment(data?.eventDetails.endTime, "HH:mm");
      const hours = endTime.diff(startTime, "hours", true);

      return hours;
    } else {
      return null;
    }
  }, [data?.eventDetails.startTime, data?.eventDetails.endTime]);

  const days = numberOfDays > 1 ? "Days" : "Day";

  if (!data) {
    return <NoScheduleCartDownloadSyllabus />;
  }

  return (
    <span className="relative">
      <RegistrationCloseTimer closeDate={data?.eventCloseDate} />

      <Card
        outlined
        className={clsx("", {
          "!border-red-400": data?.eventCloseDate,
          "!border-primary-main": !data?.eventCloseDate,
        })}
      >
        <CardHeader className="!pb-0">
          <Typography variant="h5" render={<div />}>
            Fast Filling Schedule
          </Typography>
        </CardHeader>
        <CardContent>
          <Box className="flex gap-2 my-2">
            <span className="inline-block px-3 py-1 bg-primary-main/15 text-primary-main rounded-full text-xs font-medium transition-transform hover:scale-105">
              {weekLabel}
            </span>

            {data.isEarlyBirdOfferApplied && (
              <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium transition-transform hover:scale-105">
                Early bird
              </span>
            )}

            <span className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium transition-transform hover:scale-105">
              <Timer size={12} className="mr-1" />
              filling fast
            </span>
          </Box>
          {data?.hasMappedService && (
            <Typography
              variant="h5"
              render={<span />}
              className="block mb-2 font-semibold"
            >
              {data?.eventName}
            </Typography>
          )}
          <h3 className="font-bold my-2 text-gray-900">
            {formattedDate} {`(${numberOfDays} ${days})`}
          </h3>
          <Typography variant="body2" className="!text-gray-900 mb-2">
            {data?.eventDetails?.startTime} - {data?.eventDetails?.endTime}{" "}
            {data?.eventDetails?.timezone}
            <span>({hours} Hrs / Day)</span>
          </Typography>
          <Box className="flex items-center space-x-2 mb-2">
            {data?.modeOfTraining === "online" ? (
              <Monitor className="text-primary-main" size={20} />
            ) : (
              <Building className="text-primary-main" size={20} />
            )}

            <Typography variant="body2">{data?.modeOfTraining}</Typography>
          </Box>
          {data?.eventDetails?.trainer && (
            <Box className="flex items-center space-x-2 mb-2">
              <User className="text-primary-main" size={20} />
              <Typography variant="body2">
                By {data?.eventDetails?.trainer?.name}
              </Typography>
            </Box>
          )}

          <Box className="mt-3 flex items-center gap-2 justify-between">
            <Box className="flex flex-col flex-1">
              <Typography
                variant="h5"
                render={<span />}
                className="font-bold !text-lg"
              >
                {formatCurrency(
                  data.eventDetails.sellingPriceNumber * quantity,
                  data.eventDetails.currency
                )}
              </Typography>
              <Typography
                variant="body1"
                render={<span />}
                className="line-through text-gray-500 !text-xs"
              >
                {formatCurrency(
                  data.eventDetails.priceNumber * quantity,
                  data.eventDetails.currency
                )}
              </Typography>
            </Box>
            <Box>
              <NumberField
                label="Number of learners"
                noLabel
                min={1}
                onValueChange={(value) => setQuantity(value || 1)}
                inputProps={{
                  readOnly: true,
                  className: "!w-14",
                }}
              />
            </Box>
          </Box>
          <Box className="my-2">
            {data.eventDetails.discountValue && (
              <span className="text-sm font-medium text-green-600">
                Save {data.eventDetails.discountPercent} (
                {formatCurrency(
                  data.eventDetails.discountValueNumber * quantity,
                  data.eventDetails.currency
                )}
                )
              </span>
            )}
          </Box>
          <Box className="mb-2">
            <span className="text-sm font-medium text-green-600 flex items-center gap-1">
              <CheckCircle size={18} /> No processing fee
            </span>
          </Box>

          <Button
            color="primary"
            fullWidth
            loading={status === "loading"}
            onClick={() =>
              addToCart({
                serviceId: data.serviceId,
                eventId: data.id,
                eventDetailId: data.eventDetails.id,
                quantity: quantity || 1,
              })
            }
          >
            Enroll Now
          </Button>

          {/* <Typography
          variant="body2"
          color="primary"
          align="center"
          className="block mt-4 cursor-pointer"
          render={<a href="#date-and-price" />}
        >
          View all Schedules &gt;
        </Typography> */}
        </CardContent>
      </Card>
    </span>
  );
};

export default FastFillingSchedule;
